// src/components/IndividualSetupDialog.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, GraduationCap } from 'lucide-react';

const YEAR_GROUPS = [
  'Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6',
] as const;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function IndividualSetupDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { refreshProfile, profile, user } = useAuth();

  const firstName = profile?.full_name?.split(' ')[0] ?? user?.user_metadata?.full_name?.split(' ')[0] ?? null;

  const [schoolName, setSchoolName] = useState('');
  const [yearGroup, setYearGroup] = useState('');
  const [className, setClassName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = yearGroup && className.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        'individual-teacher-setup',
        { body: { schoolName: schoolName.trim() || 'My Classes', yearGroup, className: className.trim() } },
      );
      if (fnError) {
        let message = fnError.message;
        try {
          const body = typeof data === 'object' && data !== null ? data : await (fnError as any).context?.json?.();
          if (body?.error) message = body.error;
        } catch { /* ignore parse errors */ }
        throw new Error(message);
      }
      await refreshProfile();
      navigate('/individual-billing');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
              <GraduationCap className="h-3.5 w-3.5" /> Individual teacher
            </span>
          </div>
          <DialogTitle>
            {firstName ? `Welcome, ${firstName}! Let's set up your first class.` : "Let's set up your first class."}
          </DialogTitle>
          <DialogDescription>
            Add your year group and class name to get started. You can create more classes later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="school-name">Workspace name <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input
              id="school-name"
              placeholder="e.g. My Classes"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">This is just a label for your workspace — not shared with anyone.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="year-group">Year group <span className="text-destructive">*</span></Label>
            <Select value={yearGroup} onValueChange={setYearGroup} disabled={submitting}>
              <SelectTrigger id="year-group">
                <SelectValue placeholder="Select year group" />
              </SelectTrigger>
              <SelectContent>
                {YEAR_GROUPS.map((yg) => (
                  <SelectItem key={yg} value={yg}>{yg}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="class-name">Class name <span className="text-destructive">*</span></Label>
            <Input
              id="class-name"
              placeholder="e.g. Tuesday Group"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting} className="w-full">
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Setting up…</> : 'Create my class'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
